import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { htmlToMarkdown } from "./markdown";
import { ActiveFormats, EditorHandle, FormatCommand, noActiveFormats } from "./types";

interface EditorProps {
  // Fires whenever the editor toggles between "has something worth posting"
  // and "empty". The contenteditable DOM is the source of truth for content;
  // the parent only needs this boolean to enable/disable Post.
  onEmptyChange: (isEmpty: boolean) => void;
  onUrlPasted: (url: string) => boolean;
  onActiveFormatsChange: (formats: ActiveFormats) => void;
  placeholder: string;
  ariaLabel: string;
  initialHTML?: string;
}

const BARE_URL_PATTERN = /^https?:\/\/\S+$/i;

const Editor = forwardRef<EditorHandle, EditorProps>(
  (
    {
      onEmptyChange,
      onUrlPasted,
      onActiveFormatsChange,
      placeholder,
      ariaLabel,
      initialHTML,
    },
    ref,
  ) => {
    const editableRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!initialHTML || !editableRef.current) return;

      editableRef.current.innerHTML = initialHTML;
      onEmptyChange(!(editableRef.current.textContent || "").trim());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      // Normalizes bold/italic to semantic <b>/<i> tags across browsers
      // instead of inline font-weight/font-style spans. The literal boolean
      // (not the string "false") is what browsers actually expect here;
      // the cast only satisfies TS's stricter `execCommand` typing.
      document.execCommand("styleWithCSS", false, false as unknown as string);
    }, []);

    // Chromium only un-wraps the structurally-first <li> when toggling a
    // list off from a cursor position on a later item, leaving the rest as
    // stray siblings outside the <ul>/<ol>. Selecting the whole list first
    // forces the toggle to apply uniformly to every item.
    const expandSelectionToWholeList = () => {
      const selection = window.getSelection();
      const anchorNode = selection?.anchorNode;
      if (!selection || !anchorNode) return;

      const anchorElement =
        anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : (anchorNode as Element);
      const listEl = anchorElement?.closest("ul, ol");
      if (!listEl) return;

      const range = document.createRange();
      range.selectNodeContents(listEl);
      selection.removeAllRanges();
      selection.addRange(range);
    };

    const updateActiveFormats = () => {
      if (document.activeElement !== editableRef.current) {
        onActiveFormatsChange(noActiveFormats);
        return;
      }

      onActiveFormatsChange({
        isBold: document.queryCommandState("bold"),
        isItalic: document.queryCommandState("italic"),
        isBulleted: document.queryCommandState("insertUnorderedList"),
        isNumbered: document.queryCommandState("insertOrderedList"),
      });
    };

    useEffect(() => {
      document.addEventListener("selectionchange", updateActiveFormats);
      return () => document.removeEventListener("selectionchange", updateActiveFormats);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        getMarkdown: () => htmlToMarkdown(editableRef.current?.innerHTML || ""),
        clear: () => {
          if (editableRef.current) editableRef.current.innerHTML = "";
        },
        execFormat: (command: FormatCommand) => {
          editableRef.current?.focus();

          const isListCommand = command === "insertUnorderedList" || command === "insertOrderedList";
          const isTogglingListOff = isListCommand && document.queryCommandState(command);
          if (isTogglingListOff) expandSelectionToWholeList();

          document.execCommand(command);

          // expandSelectionToWholeList leaves the whole former list selected;
          // collapse to a cursor so the next keystroke doesn't overwrite it.
          if (isTogglingListOff) window.getSelection()?.collapseToEnd();

          updateActiveFormats();
        },
      }),
      [],
    );

    const onInput = () => {
      const el = editableRef.current;
      if (!el) return;

      // Deleting the last character leaves browser debris (<br>, empty <div>)
      // that defeats the `:empty` placeholder. Wipe it when nothing real is
      // left — but keep an actual list intact so a mid-edit list survives.
      if (!el.textContent?.trim() && !el.querySelector("li")) {
        el.innerHTML = "";
      }

      onEmptyChange(!(el.textContent || "").trim());
    };

    const onPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
      const raw = event.clipboardData.getData("text");
      const trimmed = raw.trim();

      if (BARE_URL_PATTERN.test(trimmed) && !/\s/.test(trimmed) && onUrlPasted(trimmed)) {
        event.preventDefault();
        return;
      }

      // Force plain-text-only insertion so pasted rich content (from a
      // webpage, a Word doc, etc.) never carries tags/structure outside the
      // four marks this toolbar supports.
      event.preventDefault();
      document.execCommand("insertText", false, raw);
    };

    return (
      <div
        ref={editableRef}
        className="community-composer__input"
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        data-placeholder={placeholder}
        onInput={onInput}
        onPaste={onPaste}
      />
    );
  },
);

Editor.displayName = "Editor";

export default Editor;
